import { Info, Star } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { QUADRANT_CHIP } from './MatrixPicker'
import { QUADRANTS, type QuadrantKey } from './quadrants'

/** URL keys for the board's one-click filters. Kept here so the toolbar and the board read the same names. */
export const STAR_PARAM = 'star'
export const MATRIX_PARAM = 'mx'
export type MatrixPick = QuadrantKey | 'unset'

export function readMatrixParam(raw: string | null): MatrixPick[] {
  const valid = new Set<string>([...QUADRANTS.map((q) => q.key), 'unset'])
  return (raw ?? '').split(',').filter((v): v is MatrixPick => valid.has(v))
}

/**
 * Today's star and the four Eisenhower boxes, always on screen: narrowing to "what I starred" or
 * "what is urgent and important" is one click, not a trip through the Filters panel.
 */
export function QuickFilters({ starred, picks, onStar, onPicks }: { starred: boolean; picks: MatrixPick[]; onStar: (on: boolean) => void; onPicks: (next: MatrixPick[]) => void }) {
  const toggle = (key: MatrixPick) => onPicks(picks.includes(key) ? picks.filter((k) => k !== key) : [...picks, key])
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Quick filters">
      <button
        type="button"
        aria-pressed={starred}
        title="Only the items you starred for today"
        onClick={() => onStar(!starred)}
        className={cn('inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors', starred ? 'border-brand-accent bg-brand-soft text-brand-dark' : 'border-border bg-card text-text-secondary hover:bg-muted')}
      >
        <Star className={cn('size-3.5', starred && 'fill-brand-accent text-brand-accent')} />
        Starred
      </button>
      <span className="mx-0.5 h-5 w-px bg-border" aria-hidden />
      {QUADRANTS.map((q) => (
        <button
          key={q.key}
          type="button"
          aria-pressed={picks.includes(q.key)}
          title={`${q.title}: ${q.short}`}
          onClick={() => toggle(q.key)}
          className={cn(
            'inline-flex h-6 items-center rounded-sm border px-2 text-micro font-semibold transition-colors',
            picks.includes(q.key) ? cn(QUADRANT_CHIP[q.key], 'border-transparent ring-1 ring-current') : 'border-border bg-card text-text-secondary hover:bg-muted',
          )}
        >
          {q.title}
        </button>
      ))}
      <button
        type="button"
        aria-pressed={picks.includes('unset')}
        title="Items nobody has sorted yet"
        onClick={() => toggle('unset')}
        className={cn('inline-flex h-6 items-center rounded-sm border border-dashed px-2 text-micro font-semibold transition-colors', picks.includes('unset') ? 'border-brand bg-brand-soft text-brand-dark' : 'border-border text-text-muted hover:bg-muted')}
      >
        Not sorted
      </button>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" aria-label="What do Do now, Schedule, Delegate and Eliminate mean?" title="What are these?" className="grid size-6 place-items-center rounded-full text-text-muted hover:bg-muted hover:text-foreground">
            <Info className="size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[26rem] max-w-[92vw] p-3">
          <p className="text-sm font-semibold">The Eisenhower matrix</p>
          <p className="mt-1 text-tiny text-text-secondary">
            Two questions decide what to do with a piece of work: does it need attention soon (urgent), and does it move something that matters (important)? The answers give four boxes. Click one above to see only those items.
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {QUADRANTS.map((q) => (
              <div key={q.key} className="rounded-md border border-border p-2">
                <span className={cn('inline-flex h-5 items-center rounded-sm px-2 text-micro font-semibold', QUADRANT_CHIP[q.key])}>{q.title}</span>
                <p className="mt-1 text-micro text-text-muted">{q.short}</p>
                <p className="mt-1 text-tiny">{q.action}</p>
                <p className="mt-1 text-micro text-text-muted">e.g. {q.examples[0]}</p>
              </div>
            ))}
          </div>
          <p className="mt-2 text-micro text-text-muted">Set it on any card by clicking its label. The star pins an item to today’s priorities.</p>
        </PopoverContent>
      </Popover>
    </div>
  )
}
